import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  JourneyStage,
  ActiveLabOrder,
  LabOrderStatus,
  AppStatus,
  ClaimsInsightsState,
} from '../../models/careOSPrototype';
import { APP_STATUS_ORDER } from '../../models/careOSPrototype';
import { demoResultAttachments } from '../../mocks/fixtures/careOSPrototype';

/**
 * Shared state for the prototype demo controls.
 *
 * This lives in context rather than in a route component because the control
 * bar and its consumers sit in different parts of the tree: the bar renders in
 * the workspace chrome at the top of the window (`Root`, under `GlobalBar`),
 * while the state it drives is read deep inside the clinician experience
 * (`PatientProfileRoute` → `PatientProfile` → `LabsTab`). Hoisting to context
 * also means the state survives navigation, so a presenter can set up a
 * scenario before walking into the patient profile.
 *
 * The provider mounts unconditionally in `Root`; only the *bar UI* is gated
 * (see `PROTOTYPE_BAR_ENABLED`), so consumers can always read the state
 * without null-checking the context.
 */

// The bar is workspace tooling for the product & design team, so it follows
// the same non-production gate as the rest of it, and additionally requires
// the mock layer — the careOS surface these controls drive only has data
// under mocks.
export const PROTOTYPE_BAR_ENABLED =
  process.env.REACT_APP_ENV_LABEL !== 'production' &&
  process.env.REACT_APP_USE_MOCKS === 'true';

// Defaults double as the "is anything overridden?" baseline that surfaces the
// dot on the collapsed toggle (see `isDirty`).
const DEFAULT_JOURNEY_STAGE: JourneyStage = 'profile';
const DEFAULT_APP_STATUS: AppStatus = 'baseline-complete';
const DEFAULT_CLAIMS_STATE: ClaimsInsightsState = 'before-first-visit';
// The homepage shows its populated day by default — the empty state is the
// scenario you opt into, not the one you land on.
const DEFAULT_HOME_STATE: HomeState = 'filled';
// The roster leads with its varied panel — the skewed scenarios exist to
// isolate one filter at a time during a walkthrough, not to land on.
const DEFAULT_ROSTER_STATE: RosterState = 'mixed';

/**
 * Whether the provider homepage renders its populated day or its empty state.
 * Both columns already fall back to `EmptyColumnState` on an empty array (see
 * `ProviderHome/AppointmentsColumn` + `TasksColumn`), so 'empty' just withholds
 * the fixture data rather than introducing a separate rendering path.
 */
export type HomeState = 'filled' | 'empty';

/**
 * Which slice of the panel the Care roster shows.
 *
 * 'mixed' is the fixture panel as authored — every care-coordination status
 * and risk level represented, which is what makes the filter sidebar
 * demonstrable. The other two skew every member to one end so a presenter can
 * show a single filter doing its job without hunting for a matching row, and
 * 'empty' exercises the no-results state directly.
 */
export type RosterState = 'mixed' | 'needs-outreach' | 'empty';

/**
 * Stand-in order used when the Labs dropdown moves off "No active order"
 * without one having been placed through the order form. Mirrors the shape
 * `LabsTab`'s own submit handler builds so the active-order card renders
 * identically either way.
 */
function syntheticLabOrder(status: LabOrderStatus): ActiveLabOrder {
  return {
    patientId: 'p1',
    patientName: 'Nina Kelso',
    tests: ['CBC w/ Differential', 'Comprehensive Metabolic Panel'],
    vendor: 'Labcorp',
    orderedDate: 'Apr 22, 2026',
    status,
  };
}

/**
 * The active order each Stage scenario implies, applied when the Stage dropdown
 * moves. Stage drives the lab order rather than sitting beside it, so the two
 * dropdowns can't disagree about whether an order exists:
 *
 *   'review'  Pending with results attached — the state the provider reviews.
 *   'alert'   The order hasn't been placed yet; that's what the banner is for.
 *   'done'    The order was reviewed, so it's left Active Orders for the
 *             gallery (`reviewedDemoResult`, merged in by `LabsTab`).
 *   'profile' Steady state imposes nothing, leaving the Labs dropdown in
 *             charge — including a Pending order the presenter set up first.
 */
function orderForStage(stage: JourneyStage): ActiveLabOrder | null | 'unchanged' {
  switch (stage) {
    case 'review':
      return { ...syntheticLabOrder('submitted'), attachments: demoResultAttachments };
    case 'alert':
    case 'done':
      return null;
    case 'profile':
      return 'unchanged';
  }
}

/**
 * Persisted per tab so a scenario survives a full page load. That isn't
 * belt-and-braces: several routes into the patient profile still navigate with
 * `window.open(url, '_self')` (see `ProviderHome/VisitRow`, which carries a
 * "TODO production" on that call), which tears down React state. Without this,
 * a presenter's setup would evaporate the moment they clicked into a patient.
 * It also means a mid-demo refresh doesn't cost them the scenario.
 *
 * sessionStorage rather than localStorage: scoped to the tab, and gone when the
 * demo is over instead of quietly resurfacing days later.
 */
const STORAGE_KEY = 'ayble.prototypeBar.v1';

interface PersistedState {
  journeyStage: JourneyStage;
  appStatus: AppStatus;
  claimsState: ClaimsInsightsState;
  homeState: HomeState;
  rosterState: RosterState;
  activeLabOrder: ActiveLabOrder | null;
  expanded: boolean;
}

function readPersisted(): Partial<PersistedState> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<PersistedState>) : {};
  } catch {
    // Private-mode storage failures / malformed JSON shouldn't break the app.
    return {};
  }
}

// Validate on read: a stored value from an older shape (a renamed stage, a
// dropped claims scenario) would otherwise put a control in a state its
// dropdown can't display.
function restore<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

interface PrototypeState {
  /**
   * Which labs scenario is staged (see `JourneyStage`). Setting it also applies
   * that scenario's active order (`orderForStage`), so Stage and Labs describe
   * one lab order between them rather than two that can contradict.
   */
  journeyStage: JourneyStage;
  setJourneyStage: (s: JourneyStage) => void;
  activeLabOrder: ActiveLabOrder | null;
  setActiveLabOrder: (order: ActiveLabOrder | null) => void;
  appStatus: AppStatus;
  setAppStatus: (s: AppStatus) => void;
  claimsState: ClaimsInsightsState;
  setClaimsState: (s: ClaimsInsightsState) => void;
  homeState: HomeState;
  setHomeState: (s: HomeState) => void;
  rosterState: RosterState;
  setRosterState: (s: RosterState) => void;

  /**
   * Which tab the patient profile currently has open, reported up by
   * `PatientProfileRoute`. The bar needs it to decide which controls are
   * relevant: Claims/App Status belong to Overview, Labs/Stage to Labs &
   * Documents. `null` when no patient profile is mounted.
   */
  activeTab: string | null;
  setActiveTab: (tab: string | null) => void;

  /**
   * Labs is exposed to the bar as a single flat axis (`null` = no active
   * order) rather than the `ActiveLabOrder | null` object the rest of the
   * prototype passes around, so one dropdown can both create and clear an
   * order. Setting a status when no order exists synthesizes one.
   *
   * Setting it also drops Stage back to 'profile' when a scenario was staged,
   * since the scenario owned the order this is about to overwrite.
   */
  labStatus: LabOrderStatus | null;
  setLabStatus: (s: LabOrderStatus | null) => void;

  expanded: boolean;
  setExpanded: (v: boolean) => void;
  /** True when any control sits off its default — drives the collapsed dot. */
  isDirty: boolean;
  /** Returns every control to its default without collapsing the bar. */
  reset: () => void;
}

const PrototypeStateContext = createContext<PrototypeState | null>(null);

const STAGE_VALUES: readonly JourneyStage[] = ['alert', 'profile', 'review', 'done'];
const CLAIMS_VALUES: readonly ClaimsInsightsState[] = [
  'before-first-visit',
  'summary-ready',
  'steady-state',
  'no-claims-data',
];
const HOME_VALUES: readonly HomeState[] = ['filled', 'empty'];
const ROSTER_VALUES: readonly RosterState[] = ['mixed', 'needs-outreach', 'empty'];

export function PrototypeStateProvider({ children }: { children: ReactNode }) {
  // Read once on mount — later writes go through the effect below.
  const [initial] = useState(readPersisted);

  const [journeyStage, setJourneyStage] = useState<JourneyStage>(() =>
    restore(initial.journeyStage, STAGE_VALUES, DEFAULT_JOURNEY_STAGE)
  );
  const [activeLabOrder, setActiveLabOrder] = useState<ActiveLabOrder | null>(
    initial.activeLabOrder ?? null
  );
  const [appStatus, setAppStatus] = useState<AppStatus>(() =>
    restore(initial.appStatus, APP_STATUS_ORDER, DEFAULT_APP_STATUS)
  );
  const [claimsState, setClaimsState] = useState<ClaimsInsightsState>(() =>
    restore(initial.claimsState, CLAIMS_VALUES, DEFAULT_CLAIMS_STATE)
  );
  const [homeState, setHomeState] = useState<HomeState>(() =>
    restore(initial.homeState, HOME_VALUES, DEFAULT_HOME_STATE)
  );
  const [rosterState, setRosterState] = useState<RosterState>(() =>
    restore(initial.rosterState, ROSTER_VALUES, DEFAULT_ROSTER_STATE)
  );
  const [expanded, setExpanded] = useState(initial.expanded ?? false);
  // Deliberately NOT persisted: which tab is open is a fact about the live
  // session, not a scenario to restore.
  const [activeTab, setActiveTab] = useState<string | null>(null);

  useEffect(() => {
    try {
      const snapshot: PersistedState = {
        journeyStage,
        appStatus,
        claimsState,
        homeState,
        rosterState,
        // Attachments are dropped on the way out: their `fileUrl`s are
        // `createObjectURL` blobs that die with the document, so restoring them
        // would hand the UI file references that no longer resolve.
        activeLabOrder: activeLabOrder
          ? { ...activeLabOrder, attachments: undefined }
          : null,
        expanded,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // Storage being unavailable just means the scenario won't survive a
      // reload — not worth interrupting the demo over.
    }
  }, [journeyStage, appStatus, claimsState, homeState, rosterState, activeLabOrder, expanded]);

  const value = useMemo<PrototypeState>(() => {
    const setLabStatus = (status: LabOrderStatus | null) => {
      // Taking manual control of the order supersedes any staged scenario —
      // otherwise Stage would keep claiming e.g. "Review" while Labs had just
      // replaced the order it was describing.
      setJourneyStage(DEFAULT_JOURNEY_STAGE);
      if (status === null) {
        setActiveLabOrder(null);
        return;
      }
      setActiveLabOrder(current =>
        current ? { ...current, status } : syntheticLabOrder(status)
      );
    };

    const setStage = (stage: JourneyStage) => {
      setJourneyStage(stage);
      const order = orderForStage(stage);
      if (order !== 'unchanged') setActiveLabOrder(order);
    };

    return {
      journeyStage,
      setJourneyStage: setStage,
      activeLabOrder,
      setActiveLabOrder,
      appStatus,
      setAppStatus,
      claimsState,
      setClaimsState,
      homeState,
      setHomeState,
      rosterState,
      setRosterState,
      activeTab,
      setActiveTab,
      labStatus: activeLabOrder?.status ?? null,
      setLabStatus,
      expanded,
      setExpanded,
      isDirty:
        journeyStage !== DEFAULT_JOURNEY_STAGE ||
        appStatus !== DEFAULT_APP_STATUS ||
        claimsState !== DEFAULT_CLAIMS_STATE ||
        homeState !== DEFAULT_HOME_STATE ||
        rosterState !== DEFAULT_ROSTER_STATE ||
        activeLabOrder !== null,
      reset: () => {
        setJourneyStage(DEFAULT_JOURNEY_STAGE);
        setAppStatus(DEFAULT_APP_STATUS);
        setClaimsState(DEFAULT_CLAIMS_STATE);
        setHomeState(DEFAULT_HOME_STATE);
        setRosterState(DEFAULT_ROSTER_STATE);
        setActiveLabOrder(null);
      },
    };
  }, [journeyStage, activeLabOrder, appStatus, claimsState, homeState, rosterState, activeTab, expanded]);

  return (
    <PrototypeStateContext.Provider value={value}>
      {children}
    </PrototypeStateContext.Provider>
  );
}

export function usePrototypeState(): PrototypeState {
  const ctx = useContext(PrototypeStateContext);
  if (!ctx) {
    throw new Error('usePrototypeState must be used within a PrototypeStateProvider');
  }
  return ctx;
}
